import React, { useMemo } from 'react';
import { ZoomIn, ZoomOut, Maximize, Lock, Plus } from 'lucide-react';
import { NodeData, ConnectionData } from './types';
import { NodeCard } from './NodeCard';
const NODES: NodeData[] = [{
  id: 'n1',
  type: 'trigger',
  title: 'Gmail Trigger',
  label: 'Trigger',
  icon: 'Mail',
  color: 'bg-rose-500/10 text-rose-400 border border-rose-500/20',
  x: 80,
  y: 200,
  status: 'ok'
}, {
  id: 'n2',
  type: 'ai',
  title: 'Claude AI Summarise',
  label: 'AI Action',
  icon: 'Sparkles',
  color: 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
  x: 380,
  y: 200,
  status: 'ok'
}, {
  id: 'n3',
  type: 'logic',
  title: 'Filter Urgent',
  label: 'Logic',
  icon: 'Filter',
  color: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  x: 680,
  y: 200,
  status: 'ok'
}, {
  id: 'n4',
  type: 'action',
  title: 'Draft Reply',
  label: 'Action',
  icon: 'PenSquare',
  color: 'bg-sky-500/10 text-sky-400 border border-sky-500/20',
  x: 980,
  y: 200,
  status: 'ok'
}, {
  id: 'n5',
  type: 'action',
  title: 'Send',
  label: 'Action',
  icon: 'Send',
  color: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  x: 1280,
  y: 200,
  status: 'ok'
}];
const CONNECTIONS: ConnectionData[] = [{
  id: 'c1',
  source: 'n1',
  target: 'n2'
}, {
  id: 'c2',
  source: 'n2',
  target: 'n3'
}, {
  id: 'c3',
  source: 'n3',
  target: 'n4'
}, {
  id: 'c4',
  source: 'n4',
  target: 'n5'
}];
interface WorkflowCanvasProps {
  selectedNodeId: string | null;
  onSelectNode: (id: string) => void;
}
export function WorkflowCanvas({
  selectedNodeId,
  onSelectNode
}: WorkflowCanvasProps) {
  // Calculate SVG paths for connections
  const paths = useMemo(() => {
    return CONNECTIONS.map(conn => {
      const sourceNode = NODES.find(n => n.id === conn.source);
      const targetNode = NODES.find(n => n.id === conn.target);
      if (!sourceNode || !targetNode) return null;
      // Node dimensions (approximate based on NodeCard)
      const nodeWidth = 220;
      const nodeHeight = 90;
      const startX = sourceNode.x + nodeWidth;
      const startY = sourceNode.y + nodeHeight / 2;
      const endX = targetNode.x;
      const endY = targetNode.y + nodeHeight / 2;
      // Create a smooth bezier curve
      const controlPointOffset = Math.max(Math.abs(endX - startX) / 2, 50);
      const d = `M ${startX} ${startY} C ${startX + controlPointOffset} ${startY}, ${endX - controlPointOffset} ${endY}, ${endX} ${endY}`;
      return {
        id: conn.id,
        d
      };
    }).filter(Boolean);
  }, []);
  return <div className="flex-1 relative bg-[#070b14] bg-grid-pattern overflow-auto" onClick={() => onSelectNode('')} data-id="element-168">
      {/* Canvas Container - large enough to scroll */}
      <div className="relative w-[1600px] h-[800px]" data-id="element-169">
        {/* Connections SVG Layer */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" data-id="element-170">
          <defs data-id="element-171">
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-172">
              <feGaussianBlur stdDeviation="3" result="blur" data-id="element-173" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-174" />
            </filter>
          </defs>

          {paths.map((path, i) => path && <g key={path.id} data-id="element-175">
                  {/* Base subtle line */}
                  <path d={path.d} fill="none" stroke="#1e293b" strokeWidth="3" data-id="element-176" />
                  {/* Animated glowing dashed line */}
                  <path id={`path-${path.id}`} d={path.d} fill="none" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 12" className="animate-flow opacity-60" filter="url(#glow)" data-id="element-177" />
                  {/* Traveling dot particle */}
                  <circle r="3" fill="#fff" filter="url(#glow)" data-id="element-178">
                    <animateMotion dur={`${2 + i * 0.2}s`} repeatCount="indefinite" path={path.d} data-id="element-179" />
                  </circle>
                </g>)}
        </svg>

        {/* Nodes Layer */}
        {NODES.map(node => <NodeCard key={node.id} node={node} isSelected={selectedNodeId === node.id} onClick={e => {
        e.stopPropagation();
        onSelectNode(node.id);
      }} data-id="element-180" />)}
      </div>

      {/* Floating Toolbar (Bottom Left) */}
      <div className="absolute bottom-6 left-6 flex items-center gap-2 bg-slate-900/80 backdrop-blur-md border border-slate-700 p-1.5 rounded-lg shadow-xl z-20" data-id="element-181">
        <button className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded transition-colors" title="Zoom In" data-id="element-182">
          <ZoomIn className="w-4 h-4" data-id="element-183" />
        </button>
        <button className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded transition-colors" title="Zoom Out" data-id="element-184">
          <ZoomOut className="w-4 h-4" data-id="element-185" />
        </button>
        <div className="w-px h-5 bg-slate-700 mx-1" data-id="element-186" />
        <button className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded transition-colors" title="Fit to Screen" data-id="element-187">
          <Maximize className="w-4 h-4" data-id="element-188" />
        </button>
        <button className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded transition-colors" title="Lock Canvas" data-id="element-189">
          <Lock className="w-4 h-4" data-id="element-190" />
        </button>
      </div>

      {/* Floating Add Node Button */}
      <button className="absolute bottom-6 right-6 flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 px-4 py-2.5 rounded-full font-semibold shadow-lg glow-sky transition-all hover:scale-105 z-20" data-id="element-191">
        <Plus className="w-5 h-5" data-id="element-192" />
        Add Node
      </button>
    </div>;
}